import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { Leaf, ArrowLeft, CheckCircle2, Upload, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";

const VISA = ["Skilled Worker", "Health & Care Worker Visa", "Senior or Specialist Worker", "Graduate Visa", "Student Visa", "Dependant Visa", "Settled / Pre-settled", "Other"];

export default function PortalApply() {
  const { id } = useParams();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(null);

  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    cv_summary: "",
    cv_file: null,
    employer1: "", role1: "", start1: "", end1: "", desc1: "",
    employer2: "", role2: "", start2: "", end2: "", desc2: "",
    ref1_name: "", ref1_rel: "", ref1_email: "",
    ref2_name: "", ref2_rel: "", ref2_email: "",
    ref3_name: "", ref3_rel: "", ref3_email: "",
    rtw_status: "british", visa_category: "",
    dbs_consent: true,
    source: "Direct",
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => { api.get(`/portal/jobs/${id}`).then(({ data }) => setJob(data)); }, [id]);

  const submit = async () => {
    if (!form.first_name || !form.last_name || !form.email || !form.phone) return toast.error("Please complete personal details");
    if (!form.cv_file) return toast.error("Please upload your CV");
    if (!form.dbs_consent) return toast.error("DBS consent is required for care roles");
    try {
      const work_history = [];
      if (form.employer1) work_history.push({ employer: form.employer1, role: form.role1, start: form.start1, end: form.end1, description: form.desc1 });
      if (form.employer2) work_history.push({ employer: form.employer2, role: form.role2, start: form.start2, end: form.end2, description: form.desc2 });
      const references = [];
      [1, 2, 3].forEach((i) => {
        const name = form[`ref${i}_name`], rel = form[`ref${i}_rel`], email = form[`ref${i}_email`];
        if (name && email) references.push({ name, relationship: rel || "Manager", email, phone: "" });
      });
      const { data } = await api.post("/portal/apply", {
        job_id: id,
        first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone,
        cv_summary: form.cv_summary, cv_file: form.cv_file, work_history, references,
        rtw_status: form.rtw_status, visa_category: form.rtw_status === "immigrant" ? form.visa_category : null,
        dbs_consent: form.dbs_consent, source: form.source,
      });
      setSubmitted(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit application"); }
  };

  const handleCvUpload = async (file) => {
    if (!file) return;
    const allowed = [".pdf", ".doc", ".docx", ".txt"];
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    if (!allowed.includes(ext)) return toast.error("Allowed file types: PDF, DOC, DOCX, TXT");
    if (file.size > 20 * 1024 * 1024) return toast.error("File too large (max 20 MB)");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/portal/upload-cv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, cv_file: data }));
      toast.success("CV uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  if (!job) return <div className="p-8 text-[var(--cc-text-soft)]">Loading…</div>;

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--cc-bg)" }}>
        <div className="cc-card p-10 max-w-md w-full text-center fade-up" data-testid="application-success">
          <div className="w-14 h-14 rounded-full mx-auto mb-5 flex items-center justify-center" style={{ background: "var(--cc-secondary)" }}>
            <CheckCircle2 className="w-7 h-7 text-white" />
          </div>
          <h2 className="font-display text-3xl font-bold mb-3">Application received</h2>
          <p className="text-[var(--cc-text-soft)] mb-6">Thank you. You'll receive a confirmation email at <strong>{submitted.tracking_email}</strong>. You can track progress any time.</p>
          <div className="flex flex-col gap-2">
            <Link to={`/track?email=${encodeURIComponent(submitted.tracking_email)}`} className="btn-primary justify-center" data-testid="track-application-button">Track application</Link>
            <Link to="/jobs" className="btn-ghost">Browse more roles</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--cc-bg)" }}>
      <Seo title={`Apply: ${job.title} | Rabah Concept`} noindex />
      <header className="cc-topbar px-8 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "var(--cc-primary)" }}>
            <Leaf className="w-4 h-4 text-white" />
          </div>
          <div className="font-display font-bold text-lg">CareConnect</div>
        </Link>
        <Link to={`/jobs/${id}`} className="btn-ghost"><ArrowLeft className="w-4 h-4 inline mr-1" />Back</Link>
      </header>

      <div className="max-w-3xl mx-auto px-8 py-12 fade-up">
        <div className="label-eyebrow mb-2">Applying for</div>
        <h1 className="font-display text-4xl font-bold mb-2">{job.title}</h1>
        <div className="text-[var(--cc-text-soft)] mb-8">{job.location} · {job.salary}</div>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-8">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className={`flex-1 h-1.5 rounded-full ${step >= n ? "bg-[var(--cc-primary)]" : "bg-[var(--cc-border)]"}`} />
          ))}
        </div>

        <div className="cc-card p-8">
          {step === 1 && (
            <div className="space-y-4" data-testid="apply-step-1">
              <h2 className="font-display text-2xl font-bold mb-2">About you</h2>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label-eyebrow block mb-1">First name</label>
                  <input className="cc-input" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="apply-first-name" /></div>
                <div><label className="label-eyebrow block mb-1">Last name</label>
                  <input className="cc-input" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="apply-last-name" /></div>
                <div><label className="label-eyebrow block mb-1">Email</label>
                  <input type="email" className="cc-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="apply-email" /></div>
                <div><label className="label-eyebrow block mb-1">Phone</label>
                  <input className="cc-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="apply-phone" /></div>
                <div className="col-span-2"><label className="label-eyebrow block mb-1">CV / Resume <span className="text-[var(--cc-accent)]">*</span></label>
                  {form.cv_file ? (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-[var(--cc-surface)] border border-[var(--cc-border)]" data-testid="cv-uploaded">
                      <FileText className="w-5 h-5 text-[var(--cc-primary)]" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{form.cv_file.filename}</div>
                        <div className="text-xs text-[var(--cc-text-soft)]">{Math.round(form.cv_file.size / 1024)} KB · Uploaded</div>
                      </div>
                      <button className="btn-ghost text-xs" onClick={() => setForm({ ...form, cv_file: null })} data-testid="cv-remove">
                        <Trash2 className="w-3.5 h-3.5" /> Replace
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center gap-2 p-6 rounded-lg border-2 border-dashed border-[var(--cc-border)] hover:border-[var(--cc-primary)] hover:bg-[var(--cc-surface)] cursor-pointer transition" data-testid="cv-dropzone">
                      <Upload className="w-6 h-6 text-[var(--cc-text-soft)]" />
                      <div className="text-sm font-medium">{uploading ? "Uploading…" : "Click to upload your CV"}</div>
                      <div className="text-xs text-[var(--cc-text-soft)]">PDF, DOC, DOCX or TXT · Max 20 MB</div>
                      <input type="file" accept=".pdf,.doc,.docx,.txt" className="hidden" onChange={(e) => handleCvUpload(e.target.files?.[0])} data-testid="cv-file-input" disabled={uploading} />
                    </label>
                  )}
                </div>
                <div className="col-span-2"><label className="label-eyebrow block mb-1">CV / About</label>
                  <textarea className="cc-textarea min-h-[100px]" value={form.cv_summary} onChange={(e) => setForm({ ...form, cv_summary: e.target.value })} placeholder="Tell us about your care experience…" /></div>
                <div><label className="label-eyebrow block mb-1">How did you hear about us?</label>
                  <select className="cc-select" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
                    <option>Direct</option><option>Indeed</option><option>LinkedIn</option><option>Referral</option><option>Other</option>
                  </select></div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="apply-step-2">
              <h2 className="font-display text-2xl font-bold mb-2">Recent work history</h2>
              <p className="text-sm text-[var(--cc-text-soft)] mb-3">Two most recent roles are sufficient for now.</p>
              {[1, 2].map((n) => (
                <div key={n} className="border border-[var(--cc-border)] rounded-xl p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="label-eyebrow block mb-1">Employer</label>
                      <input className="cc-input" value={form[`employer${n}`]} onChange={(e) => setForm({ ...form, [`employer${n}`]: e.target.value })} /></div>
                    <div><label className="label-eyebrow block mb-1">Role</label>
                      <input className="cc-input" value={form[`role${n}`]} onChange={(e) => setForm({ ...form, [`role${n}`]: e.target.value })} /></div>
                    <div><label className="label-eyebrow block mb-1">Start (YYYY-MM)</label>
                      <input className="cc-input" placeholder="2022-03" value={form[`start${n}`]} onChange={(e) => setForm({ ...form, [`start${n}`]: e.target.value })} /></div>
                    <div><label className="label-eyebrow block mb-1">End</label>
                      <input className="cc-input" placeholder="Present" value={form[`end${n}`]} onChange={(e) => setForm({ ...form, [`end${n}`]: e.target.value })} /></div>
                  </div>
                  <div><label className="label-eyebrow block mb-1">Description</label>
                    <textarea className="cc-textarea min-h-[60px]" value={form[`desc${n}`]} onChange={(e) => setForm({ ...form, [`desc${n}`]: e.target.value })} /></div>
                </div>
              ))}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="apply-step-3">
              <h2 className="font-display text-2xl font-bold mb-2">References</h2>
              <p className="text-sm text-[var(--cc-text-soft)] mb-3">CQC safer recruitment requires 3 references — typically managers from your most recent roles.</p>
              {[1, 2, 3].map((n) => (
                <div key={n} className="border border-[var(--cc-border)] rounded-xl p-4 grid grid-cols-3 gap-3">
                  <div><label className="label-eyebrow block mb-1">Name</label>
                    <input className="cc-input" value={form[`ref${n}_name`]} onChange={(e) => setForm({ ...form, [`ref${n}_name`]: e.target.value })} /></div>
                  <div><label className="label-eyebrow block mb-1">Relationship</label>
                    <input className="cc-input" placeholder="Line Manager" value={form[`ref${n}_rel`]} onChange={(e) => setForm({ ...form, [`ref${n}_rel`]: e.target.value })} /></div>
                  <div><label className="label-eyebrow block mb-1">Email</label>
                    <input type="email" className="cc-input" value={form[`ref${n}_email`]} onChange={(e) => setForm({ ...form, [`ref${n}_email`]: e.target.value })} /></div>
                </div>
              ))}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4" data-testid="apply-step-4">
              <h2 className="font-display text-2xl font-bold mb-2">Right to Work & Consent</h2>
              <div>
                <label className="label-eyebrow block mb-2">Right to Work in the UK</label>
                <div className="grid grid-cols-2 gap-3">
                  <button className={`cc-card p-4 text-left ${form.rtw_status === "british" ? "ring-2 ring-[var(--cc-primary)]" : ""}`} onClick={() => setForm({ ...form, rtw_status: "british" })} data-testid="apply-rtw-british">
                    <div className="font-semibold">British / Settled</div>
                    <div className="text-xs text-[var(--cc-text-soft)] mt-1">UK citizen or with settled/pre-settled status</div>
                  </button>
                  <button className={`cc-card p-4 text-left ${form.rtw_status === "immigrant" ? "ring-2 ring-[var(--cc-primary)]" : ""}`} onClick={() => setForm({ ...form, rtw_status: "immigrant" })} data-testid="apply-rtw-immigrant">
                    <div className="font-semibold">Visa holder</div>
                    <div className="text-xs text-[var(--cc-text-soft)] mt-1">I hold a valid UK visa to work</div>
                  </button>
                </div>
              </div>
              {form.rtw_status === "immigrant" && (
                <div>
                  <label className="label-eyebrow block mb-1">Visa category</label>
                  <select className="cc-select" value={form.visa_category} onChange={(e) => setForm({ ...form, visa_category: e.target.value })}>
                    <option value="">Select…</option>
                    {VISA.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
              )}
              <label className="flex gap-2 items-start mt-4 cursor-pointer">
                <input type="checkbox" className="mt-1" checked={form.dbs_consent} onChange={(e) => setForm({ ...form, dbs_consent: e.target.checked })} data-testid="apply-dbs-consent" />
                <span className="text-sm">I consent to an Enhanced DBS check being undertaken as part of CareConnect's CQC safer-recruitment process.</span>
              </label>
            </div>
          )}
        </div>

        <div className="flex justify-between mt-5">
          {step > 1 ? <button className="btn-ghost" onClick={() => setStep(step - 1)}>Back</button> : <div />}
          {step < 4 ? (
            <button className="btn-primary" onClick={() => setStep(step + 1)} data-testid="apply-next-button">Next step</button>
          ) : (
            <button className="btn-primary" onClick={submit} data-testid="apply-submit-button">Submit application</button>
          )}
        </div>
      </div>
    </div>
  );
}
